import { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { extractDomain, isPublicEmailDomain } from '../../lib/email/public-domains';
import { PhoneNumber } from '../phone/PhoneNumber';

export interface LinkerSelection {
  customerId: string;
  customerType: 'account' | 'lead';
  customerName: string;
  contactId?: string;
  contactName?: string;
  rememberDomain?: boolean;
}

interface ContactResult {
  id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
}

interface AccountResult {
  id: string;
  type: 'account_business' | 'account_person';
  name: string;
  email: string;
  phone: string;
  city: string;
  postcode: string;
  blacklisted?: boolean;
  blacklistReden?: string | null;
  contacts?: ContactResult[];
}

interface LeadResult {
  id: string;
  type: 'lead';
  name: string;
  email: string;
  phone: string;
  city: string;
  postcode: string;
}

type SearchResult = AccountResult | LeadResult;

interface Props {
  initialQuery?: string;
  fromEmail?: string;
  onSelect: (selection: LinkerSelection) => void | Promise<void>;
  onCancel: () => void;
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export function CustomerLinker({ initialQuery = '', fromEmail, onSelect, onCancel }: Props) {
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [rememberDomain, setRememberDomain] = useState(false);
  const [expandedAccountId, setExpandedAccountId] = useState<string | null>(null);
  const [extraContacts, setExtraContacts] = useState<Record<string, ContactResult[]>>({});
  const [loadingMoreId, setLoadingMoreId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const fromDomain = fromEmail ? extractDomain(fromEmail) : null;
  const domainIsPublic = fromDomain ? isPublicEmailDomain(fromDomain) : true;
  const canRememberDomain = !!fromDomain && !domainIsPublic;

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const token = await getToken();
        const res = await fetch(`/api/customers/search?q=${encodeURIComponent(query.trim())}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.ok) {
          const data = await res.json();
          setResults((data.results || []) as SearchResult[]);
        } else {
          setResults([]);
        }
      } catch (err) {
        console.error('[CustomerLinker] search error:', err);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  async function loadMoreContacts(accountId: string) {
    setLoadingMoreId(accountId);
    try {
      const token = await getToken();
      const res = await fetch(`/api/customers/contacts-by-account?id=${encodeURIComponent(accountId)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const data = await res.json();
        setExtraContacts((prev) => ({ ...prev, [accountId]: data.contacts || [] }));
      }
    } catch (err) {
      console.error('[CustomerLinker] loadMoreContacts error:', err);
    } finally {
      setLoadingMoreId(null);
    }
  }

  async function handleSelect(selection: LinkerSelection) {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await onSelect({
        ...selection,
        rememberDomain: canRememberDomain ? rememberDomain : false,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const businessAccounts = results.filter((r): r is AccountResult => r.type === 'account_business');
  const personAccounts = results.filter((r): r is AccountResult => r.type === 'account_person');
  const leads = results.filter((r): r is LeadResult => r.type === 'lead');

  return (
    <div className="flex flex-col gap-4">
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Zoek op naam, email, telefoon, postcode of WO-nummer..."
          className="w-full px-4 py-3 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          autoFocus
        />
        {isSearching && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {query.trim().length >= 2 && !isSearching && results.length === 0 && (
        <p className="text-sm text-slate-500">Geen resultaten gevonden.</p>
      )}

      <div className="max-h-[50vh] overflow-y-auto flex flex-col gap-4">
        {businessAccounts.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Bedrijven
            </h4>
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              {businessAccounts.map((acc) => {
                const contacts = extraContacts[acc.id] ?? acc.contacts ?? [];
                const isExpanded = expandedAccountId === acc.id;
                return (
                  <div key={acc.id} className="bg-white">
                    <button
                      type="button"
                      disabled={isSubmitting}
                      onClick={() => handleSelect({
                        customerId: acc.id,
                        customerType: 'account',
                        customerName: acc.name,
                      })}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors disabled:opacity-50"
                    >
                      <div className="flex items-center gap-2">
                        <span aria-hidden>🏢</span>
                        <span className="font-medium text-sm text-slate-900">{acc.name}</span>
                        {acc.blacklisted && (
                          <span
                            className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700"
                            title={acc.blacklistReden || 'Klant staat op de blacklist'}
                          >
                            ⛔ Blacklist
                          </span>
                        )}
                      </div>
                      {acc.city && (
                        <p className="text-xs text-slate-500 mt-0.5">{acc.postcode} {acc.city}</p>
                      )}
                    </button>
                    {contacts.length > 0 && (
                      <div className="pl-10 pr-4 pb-2 flex flex-col gap-1">
                        {contacts.map((c) => (
                          <button
                            key={c.id}
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => handleSelect({
                              customerId: acc.id,
                              customerType: 'account',
                              customerName: acc.name,
                              contactId: c.id,
                              contactName: c.name,
                            })}
                            className="text-left text-xs rounded-md px-2 py-1.5 hover:bg-indigo-50 disabled:opacity-50"
                          >
                            <span className="font-medium text-slate-800">{c.name}</span>
                            {c.title && <span className="text-slate-500"> — {c.title}</span>}
                            {c.email && <span className="text-slate-400 ml-2">{c.email}</span>}
                          </button>
                        ))}
                        {!isExpanded && (acc.contacts?.length ?? 0) >= 5 && (
                          <button
                            type="button"
                            onClick={() => { setExpandedAccountId(acc.id); loadMoreContacts(acc.id); }}
                            disabled={loadingMoreId === acc.id}
                            className="text-xs text-indigo-600 hover:text-indigo-700 self-start mt-1 disabled:opacity-50"
                          >
                            {loadingMoreId === acc.id ? 'Laden...' : 'Meer contacten...'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {personAccounts.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Particulieren
            </h4>
            <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
              {personAccounts.map((acc) => (
                <button
                  key={acc.id}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleSelect({
                    customerId: acc.id,
                    customerType: 'account',
                    customerName: acc.name,
                  })}
                  className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center gap-2">
                    <span aria-hidden>👤</span>
                    <span className="font-medium text-sm text-slate-900">{acc.name}</span>
                    {acc.blacklisted && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700"
                        title={acc.blacklistReden || 'Klant staat op de blacklist'}
                      >
                        ⛔ Blacklist
                      </span>
                    )}
                  </div>
                  <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
                    {acc.email && <span>{acc.email}</span>}
                    {acc.phone && <PhoneNumber number={acc.phone} sourceContext="customer-linker" stopPropagation />}
                    {acc.city && <span>{acc.postcode} {acc.city}</span>}
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}

        {leads.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Leads
            </h4>
            <div className="border border-amber-200 rounded-lg divide-y divide-amber-100 bg-amber-50/30">
              {leads.map((lead) => (
                <button
                  key={lead.id}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => handleSelect({
                    customerId: lead.id,
                    customerType: 'lead',
                    customerName: lead.name,
                  })}
                  className="w-full text-left px-4 py-3 hover:bg-amber-50 transition-colors disabled:opacity-50"
                >
                  <div className="flex items-center gap-2">
                    <span aria-hidden>🆕</span>
                    <span className="font-medium text-sm text-slate-900">{lead.name}</span>
                  </div>
                  <div className="flex gap-3 text-xs text-slate-500 mt-0.5">
                    {lead.email && <span>{lead.email}</span>}
                    {lead.phone && <PhoneNumber number={lead.phone} sourceContext="customer-linker" stopPropagation />}
                  </div>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>

      {fromDomain && (
        <div className="pt-2 border-t border-slate-200">
          {canRememberDomain ? (
            <label className="flex items-start gap-2 text-xs text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberDomain}
                onChange={(e) => setRememberDomain(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Onthoud dat <code className="font-mono text-[11px]">@{fromDomain}</code> altijd naar deze klant gaat
                <span className="block text-slate-400 text-[11px] mt-0.5">
                  Volgende emails van dit domein worden automatisch gekoppeld.
                </span>
              </span>
            </label>
          ) : (
            <p className="text-xs text-slate-400 italic">
              Dit is een publiek emaildomein ({fromDomain}) — kan niet aan één klant gekoppeld worden.
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
        >
          Annuleren
        </button>
      </div>
    </div>
  );
}
